import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { ADMIN, CATEGORIES } from "../config/apiConfig";
import axios from "../config/axiosInstance";
import { useApplication } from "../contexts/ApplicationContext";
import { formatToReal } from "../utils/utils";
import {
  Alert,
  Badge,
  Button,
  Spinner,
  TextInput,
  ToggleSwitch,
} from "flowbite-react";
import {
  HiOutlineDocumentText,
  HiSearch,
  HiChevronDown,
  HiChevronUp,
} from "react-icons/hi";
import { MdPictureAsPdf } from "react-icons/md";
import AutoCompleteInput from "../components/inputs/Autocomplete";

/* ===========================================
 * Helpers visuais
 * =========================================== */
const SkeletonRow = () => (
  <div className="animate-pulse">
    <div className="h-10 bg-gray-100 rounded-md" />
  </div>
);

const useDebounced = (value, delay = 350) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const CategoryHeader = ({ name, count, open, onToggle }) => (
  <button
    onClick={onToggle}
    className="w-full group flex items-center justify-between py-2.5"
  >
    <div className="flex items-center gap-2">
      <span className="text-sm font-semibold uppercase tracking-wide text-emerald-700">
        {name}
      </span>
      <Badge color="gray" size="sm">
        {count} {count === 1 ? "consulta" : "consultas"}
      </Badge>
    </div>
    <span className="text-gray-400 group-hover:text-gray-600 transition">
      {open ? (
        <HiChevronUp className="w-5 h-5" />
      ) : (
        <HiChevronDown className="w-5 h-5" />
      )}
    </span>
  </button>
);

const QueryRow = ({ query }) => (
  <tr className="hover:bg-gray-50 transition">
    <td className="px-4 py-3">
      <div className="flex flex-col">
        <span className="font-medium text-gray-800">{query?.name}</span>
        {query?.description ? (
          <span className="text-[12px] text-gray-500 mt-0.5 line-clamp-1">
            {query.description}
          </span>
        ) : null}
      </div>
    </td>
    <td className="px-4 py-3 text-right">
      <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 border border-emerald-100">
        {formatToReal(query?.price)}
      </span>
    </td>
  </tr>
);

const QueryCard = ({ query }) => (
  <div className="rounded-lg border border-gray-100 p-3 bg-white hover:shadow-sm transition">
    <div className="text-sm font-medium text-gray-800">{query?.name}</div>
    {query?.description ? (
      <div className="text-xs text-gray-500 mt-1 line-clamp-2">
        {query.description}
      </div>
    ) : null}
    <div className="mt-3">
      <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 border border-emerald-100">
        {formatToReal(query?.price)}
      </span>
    </div>
  </div>
);

const isPowerUser = (user) => user?.permissionId === "POWER_USER";
const isRepresentante = (user) => user?.companyTypeId === "REPRESENTANTE";

/* ===========================================
 * Componente principal
 * =========================================== */
const RepresentativeTablePage = ({ isActive = true }) => {
  const { user } = useApplication();
  const myCompanyId = user?.Company?.id;

  // Papéis
  const power = isPowerUser(user);
  const rep = isRepresentante(user);
  const canPickCompany = power || rep;

  // Representante selecionado (para POWER_USER mostramos seletor; para representante fixamos)
  const [selectedRepresentative, setSelectedRepresentative] = useState(
    rep
      ? {
          label: user?.Company?.name || user?.Company?.socialReason,
          value: myCompanyId,
        }
      : { label: "Todos os representantes", value: "" }
  );

  // Cliente selecionado
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Área do PDF
  const pdfAreaRef = useRef(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  // ====== REPRESENTANTES (apenas POWER_USER) ======
  const {
    isLoading: isLoadingRepresentatives,
    error: errorRepresentatives,
    data: listRepresentatives,
  } = useQuery(
    ["listRepresentativesForPrices"],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_REPRESENTATIVES}?itemsPerPage=-1`
      );
      const reps = data?.data?.data || [];
      return [
        { label: "Todos os representantes", value: "" },
        {
          label: user?.Company?.name || user?.Company?.socialReason,
          value: user?.companyId,
        },
        ...reps.map((r) => ({
          value: r.id,
          label: r?.name || r?.socialReason,
        })),
      ];
    },
    { enabled: isActive && power }
  );

  // ====== CLIENTES (igual ao seu AdminHistoryTable) ======
  const {
    isLoading: isLoadingCustomers,
    error: errorCustomers,
    data: listCustomers,
  } = useQuery(
    ["listCustomersForPrices", selectedRepresentative],
    async () => {
      // mesma request que você usa:
      const { data } = await axios.get(
        `${ADMIN.GET_CUSTOMERS}?itemsPerPage=-1&representativeId=${
          selectedRepresentative?.value || ""
        }`
      );
      if (!data?.data?.data) throw new Error("Erro ao buscar clientes");
      let customers = data.data.data.map((customer) => ({
        label:
          customer.typeCustomer === "CPF"
            ? customer.name
            : customer.socialReason,
        value: customer.id,
      }));
      // adiciona o "placeholder" caso queira listar todos em outros contextos
      customers = customers; // aqui não incluí "Todos os clientes" porque precisamos de um único cliente para carregar a tabela
      return customers;
    },
    { enabled: isActive && canPickCompany }
  );

  // Auto-seleciona primeiro cliente quando lista carrega (se nenhum escolhido)
  useEffect(() => {
    if (!canPickCompany) return;
    if (
      !isLoadingCustomers &&
      Array.isArray(listCustomers) &&
      listCustomers.length > 0
    ) {
      if (!selectedCustomer) {
        setSelectedCustomer(listCustomers[0]);
      }
    }
  }, [canPickCompany, isLoadingCustomers, listCustomers, selectedCustomer]);

  // ====== CATEGORIAS ======
  const {
    isLoading: isLoadingCategories,
    error: errorCategories,
    data: categories,
  } = useQuery(
    ["categoriesForPrices"],
    async () => {
      const { data } = await axios.get(`${CATEGORIES.GET_ALL}?itemsPerPage=-1`);
      return data?.data?.data || [];
    },
    { enabled: isActive, refetchOnWindowFocus: false }
  );

  // ====== QUERIES DA EMPRESA ALVO ======
  const targetCompanyId = canPickCompany
    ? selectedCustomer?.value
    : myCompanyId;

  const {
    isLoading: isLoadingCompanyQueries,
    error: errorCompanyQueries,
    data: companyQueries,
  } = useQuery(
    ["companyQueriesForPrices", targetCompanyId],
    async () => {
      const { data: items } = await axios.get(
        `${ADMIN.GET_COMPANY_QUERIES}/${targetCompanyId}`
      );
      return items.data;
    },
    { enabled: isActive && !!targetCompanyId, refetchOnWindowFocus: false }
  );

  // ======= UI State =======
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 300);
  const [onlyWithQueries, setOnlyWithQueries] = useState(true);
  const [openCats, setOpenCats] = useState({}); // { [catId]: boolean }

  // ======= Derived =======
  const normalized = (s) =>
    (s || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const grouped = useMemo(() => {
    if (!categories || !companyQueries) return [];
    const byCat = categories.map((category) => {
      const queries =
        companyQueries?.filter((q) => q.categoryId === category.id) || [];
      const filtered =
        debouncedSearch.trim().length > 0
          ? queries.filter((q) =>
              normalized(q?.name).includes(normalized(debouncedSearch))
            )
          : queries;
      return { ...category, queries: filtered, total: queries.length };
    });
    return onlyWithQueries ? byCat.filter((c) => c.total > 0) : byCat;
  }, [categories, companyQueries, debouncedSearch, onlyWithQueries]);

  const totals = useMemo(() => {
    const totalQueries = companyQueries?.length || 0;
    const activeCats = grouped?.length || 0;
    return { totalQueries, activeCats };
  }, [companyQueries, grouped]);

  // Abre categorias com conteúdo por padrão
  useEffect(() => {
    if (!grouped?.length) return;
    setOpenCats((prev) => {
      const next = { ...prev };
      grouped.forEach((c) => {
        if (next[c.id] === undefined) next[c.id] = c.queries?.length > 0;
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    grouped?.map((c) => c.id).join(","),
    grouped?.map((c) => c.queries?.length).join(","),
  ]);

  const loading =
    isLoadingCompanyQueries ||
    isLoadingCategories ||
    (canPickCompany &&
      (isLoadingCustomers || (power && isLoadingRepresentatives)));

  const anyError =
    errorCompanyQueries ||
    errorCategories ||
    errorCustomers ||
    errorRepresentatives;

  // ======= Exportar PDF =======
  const handleExportPdf = async () => {
    if (!pdfAreaRef.current) return;
    setExportingPdf(true);
    try {
      const [{ default: html2pdf }] = await Promise.all([
        import("html2pdf.js"),
      ]);
      const el = pdfAreaRef.current;

      const currentCustomer =
        (canPickCompany
          ? selectedCustomer?.label
          : user?.Company?.name || user?.Company?.socialReason) ||
        "tabela-consultas";

      const opt = {
        margin: [10, 10, 10, 10],
        filename: `${String(currentCustomer)
          .replace(/\s+/g, "_")
          .toLowerCase()}_precos.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      await html2pdf().set(opt).from(el).save();
    } catch (e) {
      console.error(e);
      alert("Não foi possível gerar o PDF.");
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="mt-6 space-y-6">
      {/* ======= Header ======= */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-emerald-50 flex items-center justify-center border border-emerald-100">
              <HiOutlineDocumentText className="text-emerald-700 text-2xl" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                Tabela de Preços das consultas
              </h2>
              <p className="text-sm text-gray-500">
                Valores e disponibilidade por categoria
                {canPickCompany
                  ? " para o cliente selecionado."
                  : " para sua empresa."}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2">
              <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-600">
                <span className="font-semibold">{totals.totalQueries}</span>{" "}
                consultas
              </div>
              <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-600">
                <span className="font-semibold">{totals.activeCats}</span>{" "}
                categorias visíveis
              </div>
            </div>

            <Button
              color="gray"
              onClick={handleExportPdf}
              disabled={exportingPdf}
            >
              <div className="flex items-center gap-2">
                <MdPictureAsPdf size={18} />
                <span>{exportingPdf ? "Gerando..." : "Exportar PDF"}</span>
              </div>
            </Button>

            {loading && (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Spinner size="sm" /> Carregando…
              </div>
            )}
          </div>
        </div>

        {/* ======= Filtros (com AutoCompleteInput) ======= */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Seletor de Representante (apenas POWER_USER) */}
          {power && (
            <div className="flex flex-col">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Representante
              </label>
              <div className="h-11">
                <AutoCompleteInput
                  options={listRepresentatives}
                  label=""
                  onChange={(rep) => {
                    setSelectedRepresentative(rep);
                    setSelectedCustomer(null); // reset cliente ao trocar representante
                  }}
                  value={selectedRepresentative?.value}
                  isLoading={isLoadingRepresentatives}
                  className="h-full"
                />
              </div>
              {errorRepresentatives && (
                <p className="text-xs text-red-600 mt-1">
                  Erro ao carregar representantes.
                </p>
              )}
            </div>
          )}

          {/* Seletor de Cliente (POWER_USER e REPRESENTANTE) */}
          {canPickCompany && (
            <div className="flex flex-col">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Cliente
              </label>
              <div className="h-11">
                <AutoCompleteInput
                  options={listCustomers}
                  label=""
                  onChange={(customer) => setSelectedCustomer(customer)}
                  value={selectedCustomer?.value}
                  isLoading={isLoadingCustomers}
                  className="h-full"
                />
              </div>
              {errorCustomers && (
                <p className="text-xs text-red-600 mt-1">
                  Erro ao carregar clientes.
                </p>
              )}
            </div>
          )}

          {/* Busca por nome da consulta */}
          <div className={`${canPickCompany ? "md:col-span-2" : ""}`}>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Buscar consulta
            </label>
            <div className="relative">
              <TextInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome da consulta…"
                icon={HiSearch}
                color="gray"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ======= Content (embrulhado para PDF) ======= */}
      <div
        ref={pdfAreaRef}
        className="bg-white border border-gray-200 rounded-2xl shadow-sm p-2 md:p-4"
      >
        {/* Header visual para PDF */}
        <div className="px-2 py-2 mb-2 border-b border-gray-100 print:mb-3">
          <div className="text-lg font-semibold text-gray-800">
            {canPickCompany
              ? selectedCustomer?.label || "—"
              : user?.Company?.name || user?.Company?.socialReason || "—"}
          </div>
          <div className="text-xs text-gray-500">
            Tabela de Preços • {new Date().toLocaleDateString("pt-BR")}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="p-4 space-y-3">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        )}

        {/* Errors */}
        {anyError && !loading && (
          <Alert color="failure" withBorderIcon>
            Ocorreu um erro ao carregar as informações. Tente novamente mais
            tarde.
          </Alert>
        )}

        {/* Sem cliente selecionado (quando aplicável) */}
        {!loading && !anyError && canPickCompany && !targetCompanyId && (
          <Alert color="gray" withBorderIcon>
            Selecione um cliente para visualizar a tabela de custos de
            consultas.
          </Alert>
        )}

        {/* Sem consultas */}
        {!loading &&
          !anyError &&
          targetCompanyId &&
          (!companyQueries || companyQueries.length === 0) && (
            <Alert color="gray" withBorderIcon>
              Nenhuma consulta disponível para este cliente.
            </Alert>
          )}

        {/* Grouped list */}
        {!loading &&
          !anyError &&
          targetCompanyId &&
          companyQueries?.length > 0 && (
            <div className="max-h-[68vh] overflow-y-auto pr-1 scrollbar-thin">
              {grouped.length === 0 ? (
                <Alert color="gray" withBorderIcon>
                  Nenhum resultado encontrado para{" "}
                  <strong>“{debouncedSearch}”</strong>.
                </Alert>
              ) : (
                grouped.map((cat) => {
                  const open = !!openCats[cat.id];
                  const toggle = () =>
                    setOpenCats((s) => ({ ...s, [cat.id]: !s[cat.id] }));

                  return (
                    <div
                      key={cat.id}
                      className="mb-3 rounded-xl border border-gray-100 bg-gray-50/40"
                      style={{ breakInside: "avoid" }}
                    >
                      <div className="px-3 md:px-4">
                        <CategoryHeader
                          name={cat.name}
                          count={cat.total}
                          open={open}
                          onToggle={toggle}
                        />
                      </div>

                      {open && (
                        <div className="px-3 md:px-4 pb-3">
                          {cat?.queries?.length > 0 ? (
                            <>
                              {/* Mobile grid */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:hidden">
                                {cat.queries.map((q) => (
                                  <QueryCard key={q.id || q.name} query={q} />
                                ))}
                              </div>

                              {/* Desktop table */}
                              <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-100 bg-white">
                                <table className="min-w-full text-sm text-left text-gray-700">
                                  <thead className="bg-gray-50 text-[13px]">
                                    <tr>
                                      <th className="px-4 py-3 font-semibold text-gray-600">
                                        Consulta
                                      </th>
                                      <th className="px-4 py-3 font-semibold text-gray-600 text-right">
                                        Preço
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100 bg-white">
                                    {cat.queries.map((query) => (
                                      <QueryRow
                                        key={query.id || query.name}
                                        query={query}
                                      />
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          ) : (
                            <Alert className="mt-2" color="gray" withBorderIcon>
                              Nenhuma consulta liberada nesta categoria.
                            </Alert>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
      </div>
    </div>
  );
};

export default RepresentativeTablePage;
